<i18n src="./resources/locales.json"></i18n>

<template>
  <!-- WebRTC Client -->
  <eip-janus-client
    ref="eipJanusClient"
    :servers="servers"
    :sip-user-name="sipUserName"
    :sip-display-name="sipDisplayName"
    :sip-registrar="sipRegistrar"
    :managed-audio-mode="managedAudioMode"
    @janus-ready="handleJanusReady"
    @janus-session-created="handleJanusSessionCreated"
    @janus-session-error="handleJanusSessionError"
    @janus-session-destroyed="handleJanusDestroyed"
    @show-consent-dialog="showConsentDialog"
    @hide-consent-dialog="hideConsentDialog"
    @plugin-error-message="onPluginErrorMessage"
    @plugin-message="onPluginMessage"
    @plugin-webtrc-state="onPluginWebRTCState"
    @plugin-local-track-added="onPluginLocalTrackAdded"
    @plugin-local-track-removed="onPluginLocalTrackRemoved"
    @plugin-remote-track-added="onPluginRemoteTrackAdded"
    @plugin-remote-track-removed="onPluginRemoteTrackRemoved"
    @plugin-cleanup="onPluginCleanup"
    @helper-removed="handleJanusHelperRemoved"
  ></eip-janus-client>
</template>

<script>
import '@evolveip/eip-janus-client/eip-janus-client.js';
import { createValidSipUri, findRegistrarDomain } from '@evolveip/eip-janus-client/lib/client-utils.js';
import { mapActions, mapGetters } from 'vuex';
import { debounce } from 'lodash';
import Vue from 'vue';
import EcsSoftPhoneMixin from '@/components/ecs-soft-phone/EcsSoftPhoneMixin';
import { ACTION_STATES, ACTIONS } from '@/constants/softPhone/constants';
import { AGENT_STATE } from '@/constants/agent/constants';
import { DIRECT_INCOMING_TELEPHONY, MEDIA_TYPE_TELEPHONY } from '@/constants/socket-events';

const MAX_NUMBER_OF_REGISTER_ATTEMPTS = 3;
const MAX_DTMF_QUEUE_TIME = 600;

export default {
  name: 'EcsSoftPhone',

  mixins: [EcsSoftPhoneMixin],

  data() {
    return {
      janusSessionInitialized: false,
      janusSessionInitializing: false,
      initializedCallback: null,
      registeredCallback: null,
      registerAttemptCount: 0,
      defaultCall: {
        sipHandle: null,
        registered: false,
        registering: false,
        calling: false,
        ringing: false,
        accepted: false,
        held: false,
        localTracks: {},
        localVideos: 0,
        remoteTracks: {},
        remoteVideos: 0
      },
      helpers: {},
      incomingCall: null,
      registrationRenewalTimeoutRef: null,
      eipJanusClientComponentRef: null,
      dtmQueue: {}
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('ORGANIZATION', ['orgName', 'orgShortName', 'orgId']),

    ...mapGetters('INTERACTIONS', ['getActiveTelephonyInteractions', 'getActiveInteraction', 'getPendingInteraction']),

    ...mapGetters('CONTACTS', ['getCrmContactByPhone']),

    ...mapGetters('SYSTEM', ['systemSoftPhoneSettings']),

    servers() {
      if (!this.userSoftPhoneSettings) return [];

      // Check for user level servers first
      // This is to allow the potential of overriding the server at a user level for testing/debugging purposes.
      let servers = this.userSoftPhoneSettings.servers;

      if (!servers || servers.length === 0) {
        servers = this.systemSoftPhoneSettings.servers || [];
      }

      return servers;
    },

    iceServers() {
      // Return null to internally force library defaults
      if (!this.systemSoftPhoneSettings) return null;

      return this.systemSoftPhoneSettings.iceServers;
    },

    sipUserName() {
      if (!this.userSoftPhoneSettings) return null;

      return this.userSoftPhoneSettings.sipUserName;
    },

    sipAuthUserName() {
      if (!this.userSoftPhoneSettings) return null;

      return this.userSoftPhoneSettings.sipAuthUserName;
    },

    sipDisplayName() {
      if (!this.userSoftPhoneSettings) return null;

      return this.userSoftPhoneSettings.sipDisplayName;
    },

    sipRegistrar() {
      if (!this.userSoftPhoneSettings) return null;

      return this.userSoftPhoneSettings.sipRegistrar;
    },

    managedAudioMode() {
      // NOTE: Managed audio mode should likely always be used unless there is some reason to add the audio elements directly in this app.
      return this.systemSoftPhoneSettings.managedAudioMode;
    },

    autoAcceptInvites() {
      return true; // this.systemSoftPhoneSettings.autoAcceptInvites;
    },

    forceUdp() {
      return this.systemSoftPhoneSettings.forceUdp;
    },

    debugMode() {
      if (!this.userSoftPhoneSettings) return false;

      return this.userSoftPhoneSettings.debug;
    },

    userAgent() {
      return `eAgent v.${process.env.APPLICATION_VERSION}`;
    }
  },

  created() {
    this.eipJanusClientComponentRef = this.$refs.eipJanusClient;
    this.$root.$on('soft-phone-action', this.handleSoftPhoneAction);
  },

  beforeDestroy() {
    this.$root.$off('soft-phone-action', this.handleSoftPhoneAction);
  },

  methods: {
    ...mapActions('EVENT_TIMING', ['removeEventTimingConstraintForAgentState']),

    ...mapActions('INTERACTIONS', ['upsertPendingInteraction']),

    handleSoftPhoneAction(event) {
      const action = event.action;
      const callback = event.callback || (() => {});
      const handle = this.defaultCall && this.defaultCall.sipHandle;
      this.eipJanusClientComponentRef = this.$refs.eipJanusClient;

      switch (action) {
        case ACTIONS.initialize:
          this.initializeClient(callback);
          break;
        case ACTIONS.register:
          this.registerClient(callback);
          break;
        case ACTIONS.unregister:
          this.unregisterClient({ callback, handle });
          break;
        case ACTIONS.answer:
          this.answerIncomingCall(callback);
          break;
        case ACTIONS.dial:
          const sipUri = event.sipUri;
          this.validateAndDoCall({ handle, sipUri, useVideo: false });
          break;
        case ACTIONS.decline:
          this.declineIncomingCall(callback);
          break;
        case ACTIONS.hangup:
          this.eipJanusClientComponentRef
            .performHangup({ handle })
            .then(() => callback && callback())
            .catch(console.error);
          break;
        case ACTIONS.hold:
          this.eipJanusClientComponentRef.performHold({ handle }).catch(console.error);
          break;
        case ACTIONS.unhold:
          this.eipJanusClientComponentRef.performUnHold({ handle }).catch(console.error);
          break;
        case ACTIONS.mute:
          this.muteAudio(handle);
          break;
        case ACTIONS.unmute:
          this.unmuteAudio(handle);
          break;
        case ACTIONS.dtmf:
          // eslint-disable-next-line import/no-named-as-default-member
          Vue.set(this.dtmQueue, 'keys', (this.dtmQueue.keys || '') + event.digit);
          // eslint-disable-next-line import/no-named-as-default-member
          Vue.set(this.dtmQueue, 'handle', handle);
          // eslint-disable-next-line import/no-named-as-default-member
          Vue.set(this.dtmQueue, 'inband', event.inband);
          this.queuedDtmfAction();
          break;
        case ACTIONS.destroy:
          this.destroyClient();
          break;
        default:
          // Handle any other cases or provide an error message if needed
          console.error(`Unknown action: ${action}`);
          break;
      }
    },

    queuedDtmfAction: debounce(
      function() {
        this.eipJanusClientComponentRef
          .performSendDtmf({
            handle: this.dtmQueue.handle,
            digit: this.dtmQueue.keys,
            inband: this.dtmQueue.inband
          })
          .catch(console.error);
        this.dtmQueue.keys = '';
        Vue.delete(this.dtmQueue, 'handle');
        Vue.delete(this.dtmQueue, 'inband');
      },
      MAX_DTMF_QUEUE_TIME,
      {
        leading: false,
        trailing: true
      }
    ),

    answerIncomingCall(callback) {
      const autoAcceptInvites = this.autoAcceptInvites;
      const jsep = this.incomingCall ? this.incomingCall.jsep : null;
      const handle = this.defaultCall.sipHandle;

      this.$ringtoneManager.stopInteractionRingtone();

      return this.$refs.eipJanusClient
        .performAnswerOrOffer({
          handle,
          jsep,
          autoAcceptInvites
        })
        .then(() => {
          this.incomingCall = null;
          callback && callback();
        })
        .catch(error => {
          this.incomingCall = null;
          console.error(error);
        });
    },

    declineIncomingCall(callback) {
      const handle = this.defaultCall.sipHandle;
      return this.$refs.eipJanusClient
        .performDecline({ handle })
        .then(() => {
          this.incomingCall = null;
          callback && callback();
        })
        .catch(error => {
          this.incomingCall = null;
          console.error(error);
        });
    },

    muteAudio(handle) {
      this.handleAudioMute(handle, true);
    },

    unmuteAudio(handle) {
      this.handleAudioMute(handle, false);
    },

    handleAudioMute(handle, mute = false) {
      const tracks = (handle && handle.getLocalTracks()) || [];
      tracks.forEach(track => {
        mute ? handle.muteAudio(track.mid) : handle.unmuteAudio(track.mid);
      });
    },

    handleJanusReady() {
      this.initializedCallback();
    },

    handleJanusDestroyed() {},

    handleJanusSessionError(e) {
      // const { error } = e.detail;
      // can throw some kind of alert here!
      /*
      bootbox.alert(error, () => {
        window.location.reload();
      });
       */
    },

    handleJanusSessionCreated(e) {
      // const { janus } = e.detail;
      // Not currently being used, but shown as an example.
    },

    handleJanusHelperRemoved(e) {
      // const { helperId } = e.detail;
    },

    showConsentDialog() {
      // Show some kind of consent dialog to allow media.
    },

    hideConsentDialog() {
      // Hide the consent dialog if needed.
    },

    onPluginMessage(e) {
      const { helperId, msg, jsep } = e.detail;

      // Update the call state before doing anything further
      this.updateCallState(e.detail);

      if (!helperId && helperId !== -1) {
        this.onMessage(msg, jsep);
      } else {
        // Differentiate on helper messages.  This is really up to the end user...
        // There is enough information in the message to handle individually or mixed in
        this.onHelperMessage({ msg, jsep, helperId });
      }
    },

    onPluginWebRTCState(e) {},

    /**
     * Update the call state via the updated state info.
     * @param updatedStateInfo The updated state info.
     */
    updateCallState(updatedStateInfo = {}) {
      const { defaultCall } = updatedStateInfo;

      // Update the helpers.
      if (updatedStateInfo.helpers) {
        this.helpers = updatedStateInfo.helpers;
      }

      // Update all parts of the "default call"
      if (defaultCall) {
        this.defaultCall = defaultCall;
      }
    },

    onPluginErrorMessage(e) {
      // const { msg, jsep, error } = e.detail;
      console.error('onPluginErrorMessage', e);

      if (this.defaultCall.registered) {
        // Reset status
        // TODO: Update to handle helper/sipHandle
        this.defaultCall.sipHandle.hangup();
      }
    },

    /**
     * Primary event handling of messages coming on.
     * @param msg
     * @param jsep
     */
    onMessage(msg, jsep) {
      const result = msg.result;

      if (result && result.event) {
        switch (result.event) {
          case ACTION_STATES.registrationFailed:
            this.handleRegistrationFailedEvent({ msg, jsep });
            break;
          case ACTION_STATES.registered:
            this.handleRegisteredEvent({ msg, jsep });
            break;
          case ACTION_STATES.unregistered:
            this.handleUnregisteredEvent({ msg, jsep });
            break;
          case ACTION_STATES.calling:
            this.handleCallingEvent({ msg, jsep });
            break;
          case ACTION_STATES.incomingCall:
            this.handleIncomingCallEvent({ msg, jsep });
            break;
          case ACTION_STATES.accepting:
            this.handleAcceptingEvent({ msg, jsep });
            break;
          case ACTION_STATES.progress:
            this.handleProgressEvent({ msg, jsep });
            break;
          case ACTION_STATES.accepted:
            this.handleAcceptedEvent({ msg, jsep });
            break;
          case ACTION_STATES.updatingCall:
            this.handleUpdatingCallEvent({ msg, jsep });
            break;
          case ACTION_STATES.message:
            this.handleMessageEvent({ msg, jsep });
            break;
          case ACTION_STATES.info:
            this.handleInfoEvent({ msg, jsep });
            break;
          case ACTION_STATES.notify:
            this.handleNotifyEvent({ msg, jsep });
            break;
          case ACTION_STATES.transfer:
            this.handleTransferEvent({ msg, jsep });
            break;
          case ACTION_STATES.hangup:
            this.handleHangupEvent({ msg, jsep });
            break;
          case ACTION_STATES.messageDelivery:
            this.handleMessageDeliveryEvent({ msg, jsep });
            break;
          case ACTION_STATES.missedCall:
            this.handleMissedCallEvent({ msg, jsep });
            break;
        }
      }
    },

    onHelperMessage({ msg, jsep, helperId }) {
      // const callId = msg.call_id;
      const result = msg.result;
      if (result && result.event) {
        const event = result.event;
        if (event === 'registration_failed') {
          this.removeHelper(helperId);
          return;
        }

        if (event === 'registered') {
          // Do something on helper registered?
        } else if (event === 'calling') {
        } else if (event === 'incomingcall') {
          this.handleIncomingCallEvent({ msg, jsep });
        } else if (event === 'accepting') {
          // Response to an offerless INVITE, let's wait for an 'accepted'
          this.handleAcceptingEvent({ msg, jsep });
        } else if (event === 'progress') {
          this.handleProgressEvent({ msg, jsep, helperId });
        } else if (event === 'accepted') {
          this.handleAcceptedEvent({ msg, jsep, helperId });
        } else if (event === 'updatingcall') {
          this.handleHelperUpdatingCallEvent({ msg, jsep, helperId });
        } else if (event === 'message') {
          this.handleMessageEvent({ msg, jsep, helperId });
        } else if (event === 'info') {
          this.handleInfoEvent({ msg, jsep, helperId });
        } else if (event === 'notify') {
          this.handleNotifyEvent({ msg, jsep, helperId });
        } else if (event === 'transfer') {
          this.handleHelperTransferEvent({ msg, jsep, helperId });
        } else if (event === 'hangup') {
          this.handleHelperHangupEvent({ msg, jsep, helperId });
        } else if (event === 'messagedelivery') {
          this.handleMessageDeliveryEvent({ msg, jsep });
        }
      }
    },

    handleHelperTransferEvent({ msg, jsep, helperId }) {
      /*
      const result = msg.result;
      // We're being asked to transfer the call, ask the user what to do
      const referTo = result.refer_to;
      const referredBy = result.referred_by ? result.referred_by : 'an unknown party';
      const referId = result.refer_id;
      const replaces = result.replaces;
      let extra = 'referred by ' + referredBy;
      if (replaces) extra += ', replaces call-ID ' + replaces;
      extra = extra.replace(new RegExp('<', 'g'), '&lt');
      extra = extra.replace(new RegExp('>', 'g'), '&gt');

      bootbox.confirm("Transfer the call to " + referTo + "? (" + extra + ", helper " + helperId + ")", function (result) {
        if (result) {
          // Call the person we're being transferred to
          if (!this.helpers[helperId].sipHandle.webrtcStuff.pc) {
            // Do it here
            // jq('#peer' + helperId).val(referTo).attr('disabled', true);
            this.actuallyDoCall(this.helpers[helperId].sipHandle, referTo, false, referId);
          } else if (!this.defaultCall.sipHandle.webrtcStuff.pc) {
            // Do it on the main handle
            // jq('#peer').val(referTo).attr('disabled', true);
            this.actuallyDoCall(this.defaultCall.sipHandle, referTo, false, referId);
          } else {
            // We're in a call already, use the main handle or a helper
            let h = -1;
            if (Object.keys(this.helpers).length > 0) {
              // See if any of the helpers if available
              for (let i in this.helpers) {
                if (!this.helpers[i].sipHandle.webrtcStuff.pc) {
                  h = parseInt(i);
                  break;
                }
              }
            }

            if (h !== -1) {
              // Do in this helper
              // jq('#peer' + h).val(referTo).attr('disabled', true);
              this.actuallyDoCall(this.helpers[h].sipHandle, referTo, false, referId);
            } else {
              // Create a new helper
              this.addHelper(id => {
                // jq('#peer' + id).val(referTo).attr('disabled', true);
                this.actuallyDoCall(this.helpers[id].sipHandle, referTo, false, referId);
              });
            }
          }
        } else {
          // We're rejecting the transfer
          this.$refs.eipJanusClient.performDecline({ handle: this.defaultCall.sipHandle, referId });
        }
      });
       */
    },

    handleHelperHangupEvent({ msg, jsep, helperId }) {
      // const result = msg.result;
      this.incomingCall = null;

      // Reset status
      const handle = this.helpers[helperId].sipHandle;
      handle.hangup();
    },

    onPluginLocalTrackAdded(e) {
      // Update the call state before doing anything further
      this.updateCallState(e.detail);

      /*
      // Example of how to attach local video...
      const { track, trackId, stream, helperId } = e.detail;
      // We don't do anything with our own audio, as it would cause an echo...
      if (track.kind === 'audio') {
        return;
      }

      if (!helperId && helperId !== -1) {
        // Attach stream to some video element or create as needed.
        const videoElement = document.querySelector(`#myvideot${trackId}`);
        Janus.attachMediaStream(videoElement, stream);
      } else {
        // Attach stream to some video element or create as needed.
        const videoElement = document.querySelector(`#myvideo${helperId}t${trackId}`);
        Janus.attachMediaStream(videoElement, stream);
      }
       */
    },

    onPluginLocalTrackRemoved(e) {
      // const { trackId, helperId } = e.detail;
      // Update the call state before doing anything further
      this.updateCallState(e.detail);

      /*
      if (!helperId && helperId !== -1) {
        jq(`#myvideot${trackId}`).remove();
      } else {
        jq(`#myvideo${helperId}t${trackId}`).remove();
      }
       */
    },

    onPluginRemoteTrackAdded(e) {
      // Update the call state before doing anything further
      this.updateCallState(e.detail);

      /*
      const { track, stream, mid, helperId } = e.detail;

      if (!helperId && helperId !== -1) {
        if (track.kind === 'video') {
          // Insert/reference a video element here and then attach
          // const videoElement = document.querySelector(`#peervideom${mid}`);
          // Janus.attachMediaStream(videoElement, stream);
        }
      } else {
        // Insert/reference a video element here for a helper and then attach
        // const videoElement = document.querySelector(`#peervideo${helperId}m${mid}`);
        // Janus.attachMediaStream(videoElement, stream);
      }
       */
    },

    onPluginRemoteTrackRemoved(e) {
      // Update the call state before doing anything further
      this.updateCallState(e.detail);

      // Example of how to cleanup a remote track.
      // It is expected to cleanup any old DOM elements as your app requires.
      /*
      const { mid, helperId } = e.detail;
      let peerVideoElement = null;
      if (!helperId && helperId !== -1) {
        peerVideoElement = document.querySelector(`#peervideom${mid}`);
      } else {
        peerVideoElement = document.querySelector(`#peervideo${helperId}m${mid}`);
      }

      if (peerVideoElement) {
        peerVideoElement.remove();
      }
       */
    },

    onPluginCleanup(e) {
      this.updateCallState(e.detail);
    },

    handleRegisteredEvent({ msg, jsep }) {
      this.registerAttemptCount = 0;
      this.updateIsRegisteringSoftPhone(false);
      this.updateSoftPhoneRegisteredState(true);
      this.registeredCallback && this.registeredCallback();
    },

    handleRegistrationFailedEvent({ msg, jsep }) {
      const result = msg.result;
      console.error(result);
      this.updateIsRegisteringSoftPhone(false);
      this.updateSoftPhoneRegisteredState(false);
      this.updateSoftPhoneRegistrationErrors(result);
      this.$root.$emit('submit-agent-state-change', { id: AGENT_STATE.offline });
      this.removeEventTimingConstraintForAgentState({ agentId: this.loggedInAgent.id });
      this.registeredCallback = null;

      this.$root.$emit('snackbar-message', {
        message: result.code + ' ' + result.reason,
        type: 'error',
        timeout: 5000
      });
    },

    handleUnregisteredEvent({ msg, jsep }) {
      this.updateIsRegisteringSoftPhone(false);
      this.updateSoftPhoneRegisteredState(false);
      this.registeredCallback = null;
    },

    handleCallingEvent({ msg, jsep }) {
      // Nothing here quite yet...
    },

    handleAcceptingEvent({ msg, jsep }) {
      // Nothing here quite yet.....
    },

    handleMessageEvent({ msg, jsep }) {
      /*
      const result = msg.result;
      const sender = result.displayname ? result.displayname : result.sender;
      let content = result.content;
      content = content.replace(new RegExp('<', 'g'), '&lt');
      content = content.replace(new RegExp('>', 'g'), '&gt');
      success(content, "Message from " + sender);
       */
    },

    handleInfoEvent({ msg, jsep }) {
      /*
      const result = msg.result;
      const sender = result.displayname ? result.displayname : result.sender;
      let content = result.content;
      content = content.replace(new RegExp('<', 'g'), '&lt');
      content = content.replace(new RegExp('>', 'g'), '&gt');
      info(content, "Info from " + sender);
       */
    },

    handleNotifyEvent({ msg, jsep }) {
      /*
      const result = msg.result;
      const notify = result.notify;
      const content = result.content;
      info(content, "Notify (" + notify + ")");
       */
    },

    handleTransferEvent({ msg, jsep }) {
      /*
      const result = msg.result;
      const { referTo, extra, referId } = processTransferCallDetails( { result } );
      // Example of a call transfer.
      bootbox.confirm("Transfer the call to " + referTo + "? (" + extra + ")", function (result) {
        if (result) {
          // Call the person we're being transferred to
          if (!this.defaultCall.sipHandle.webrtcStuff.pc) {
            // Do it here
            // jq('#peer').val(referTo).attr('disabled', true);
            this.actuallyDoCall(this.defaultCall.sipHandle, referTo, false, referId);
          } else {
            // We're in a call already, use a helper
            let h = -1;
            if (Object.keys(this.helpers).length > 0) {
              // See if any of the helpers if available
              for (let i in this.helpers) {
                if (!this.helpers[i].sipHandle.webrtcStuff.pc) {
                  h = parseInt(i);
                  break;
                }
              }
            }
            if (h !== -1) {
              // Do in this helper
              // jq('#peer' + h).val(referTo).attr('disabled', true);
              this.actuallyDoCall(this.helpers[h].sipHandle, referTo, false, referId);
            } else {
              // Create a new helper
              this.addHelper(id => {
                // jq('#peer' + id).val(referTo).attr('disabled', true);
                this.actuallyDoCall(this.helpers[id].sipHandle, referTo, false, referId);
              });
            }
          }
        } else {
          // We're rejecting the transfer
          this.$refs.eipJanusClient.performDecline({ handle: this.defaultCall.sipHandle, referId });
        }
      });
       */
    },

    handleHangupEvent({ msg, jsep }) {
      // msg.result: {event: 'hangup', code: 200, reason: 'to BYE'}
      // msg.result: {event: 'hangup', code: 486, reason: 'Busy Here'}
      // msg.result: {event: 'hangup', code: 200, reason: 'Session Terminated'}
      const interactionId = msg.call_id;
      const activeInteraction = this.getActiveInteraction(interactionId);
      const pendingInteraction = this.getPendingInteraction(interactionId);
      if (activeInteraction) {
        this.endDirectCallInteraction(activeInteraction);
      } else if (pendingInteraction) {
        this.endPendingDirectCallInteraction(pendingInteraction);
        this.$ringtoneManager.stopInteractionRingtone();
      }
      this.defaultCall.sipHandle.hangup();
    },

    handleMissedCallEvent({ msg, jsep }) {
      const { displayname, caller } = msg.result;
      const origin = this.extractTargetFromSipUri(caller);
      const customerFullName = (displayname || '').replaceAll(/[\\"]/g, '');
      const contact = this.getCrmContactByPhone(origin) || {};
      const name = contact.name || customerFullName;

      this.$root.$emit('snackbar-message', {
        message: name ? this.$t('directCallMissed', { name, origin }) : this.$t('directCallMissedUnknown', { origin }),
        type: 'info',
        showClose: true,
        timeout: 10000
      });
      this.$root.$emit('create-missed-interaction-notification', {
        interactionMediaType: MEDIA_TYPE_TELEPHONY,
        interactionType: DIRECT_INCOMING_TELEPHONY,
        interactionId: msg.call_id,
        businessProcessName: this.$t('directCall'),
        origin
      });
    },

    handleMessageDeliveryEvent({ msg, jsep }) {
      /*
      const result = msg.result;
      const reason = result.reason;
      const code = result.code;
      const callId = msg.call_id;
      if (code === 200) {
        success(`${callId} Delivery Status: ${code} ${reason}`);
      } else {
        error(`${callId} Delivery Status: ${code} ${reason}`);
      }
       */
    },

    handleProgressEvent({ msg, jsep }) {
      /*
      const result = msg.result;
      info("Early media...");
       */
    },

    handleAcceptedEvent({ msg, jsep }) {
      this.incomingCall = null;
    },

    handleUpdatingCallEvent({ msg, jsep }) {},

    handleHelperUpdatingCallEvent({ msg, jsep, helperId }) {},

    handleIncomingCallEvent({ msg, jsep }) {
      const callId = msg.call_id;
      const result = msg.result;
      this.incomingCall = {
        callId,
        result,
        jsep
      };

      if (jsep) {
        if (this.getActiveTelephonyInteractions.length !== 0) {
          this.$root.$emit('snackbar-message', {
            message: this.$t('directCallDeclined', { name: result.displayname || result.origin }),
            type: 'info',
            timeout: 5000
          });
          return this.declineIncomingCall();
        }

        const { callee, displayname, username } = msg.result;
        const origin = this.extractTargetFromSipUri(username);
        const destination = this.extractTargetFromSipUri(callee);
        const customerFullName = displayname.replaceAll(/[\\"]/g, '');

        const contact = this.getCrmContactByPhone(origin);

        try {
          this.$ringtoneManager.startInteractionRingtone({ isTelephony: true });
        } catch (error) {
          console.error(error);
        }

        return this.upsertPendingInteraction({
          agentId: this.loggedInAgent.id,
          interactionId: msg.call_id,
          interactionMediaType: MEDIA_TYPE_TELEPHONY,
          interactionType: DIRECT_INCOMING_TELEPHONY,
          contactId: contact && contact.id,
          remoteParty: origin,
          contact,
          customerFullName,
          origin,
          destination
        });
      }

      this.answerIncomingCall();
    },

    extractTargetFromSipUri(sipUri) {
      return sipUri
        .replace(/^sip:/, '')
        .replace(/@.*$/, '')
        .split('.')[0];
    },

    initializeClient(callback) {
      if (!this.isSoftPhoneFeatureEnabled || this.janusSessionInitialized || this.janusSessionInitializing) {
        return;
      }

      this.janusSessionInitializing = true;

      this.initializedCallback = callback;

      const servers = this.servers || [];
      const iceServers = this.iceServers;

      // Don't bother going any further if there are no configured servers
      if (servers.length === 0) {
        return;
      }

      this.establishJanusSession({ servers, iceServers, debug: this.debugMode }).then(() => {
        this.janusSessionInitialized = true;
        this.janusSessionInitializing = false;
      });
    },

    registerClient(callback) {
      if (this.registerAttemptCount >= MAX_NUMBER_OF_REGISTER_ATTEMPTS) {
        this.registerAttemptCount = 0;
        return;
      }

      if (this.isRegisteringSoftPhone) {
        return;
      }

      if (this.isSoftPhoneRegistered) {
        return;
      }

      if (!this.janusSessionInitialized || this.janusSessionInitializing) {
        return setTimeout(() => {
          this.registerClient(callback);
        }, 100);
      }

      this.registerAttemptCount++;

      this.registeredCallback = callback;
      this.updateIsRegisteringSoftPhone(true);

      const proxy = this.sipRegistrar;
      const register = {
        request: ACTIONS.register,
        username: this.sipUserName, // E.g. 'sip:6102633201.122.1@voip.lab.evolveip.net',
        display_name: this.sipDisplayName || this.loggedInAgent.name,
        proxy,
        force_udp: this.forceUdp,

        // User agent based on version number
        user_agent: this.userAgent,

        // Custom ecs properties
        ecs_session_id: sessionStorage.getItem('sessionId'),
        ecs_user_id: this.loggedInAgent.id,
        ecs_org: this.orgShortName,

        register_ttl: 100
      };

      // By default, the SIP plugin tries to extract the username part from the SIP
      // identity to register; if the username is different, you can provide it here
      if (this.sipAuthUserName !== '') {
        register.authuser = this.sipAuthUserName; // E.g. '6102633201.122';
      }

      this.$refs.eipJanusClient.performSend({ message: register });
    },

    async unregisterClient({ callback, handle }) {
      await this.$refs.eipJanusClient.performSend({
        message: { request: ACTIONS.unregister }
      });
      this.updateSoftPhoneRegisteredState(false);
      const tracks = (handle && handle.getLocalTracks()) || [];
      tracks.forEach(track => track.stop);
      callback && callback();
    },

    destroyClient() {
      this.updateSoftPhoneRegisteredState(false);
      this.$refs.eipJanusClient.destroy();
      setTimeout(this.$refs.eipJanusClient.disconnectedCallback, 200);
    },

    validateAndDoCall({ handle, sipUri, useVideo }) {
      const registarDomain = findRegistrarDomain(this.sipRegistrar);
      const uriToCall = createValidSipUri(sipUri, registarDomain);
      this.actuallyDoCall(handle, uriToCall, useVideo);
    },

    async actuallyDoCall(handle, uri, doVideo, referId) {
      try {
        await this.$refs.eipJanusClient.performCall({
          handle,
          uri,
          doVideo,
          referId,
          autoAcceptInvites: this.autoAcceptInvites
        });
      } catch (error) {
        // alert("WebRTC error... " + error.message);
      }
    },

    /**
     * Add a helper.
     * @param helperCreated Optional callback to trigger when helper is created.
     * @return {Promise<void>}
     */
    async addHelper(helperCreated) {
      try {
        const addResponse = await this.$refs.eipJanusClient.addSipHelper({ helperCreated });
        this.helpers = addResponse.helpers;
      } catch (e) {}
    },

    /**
     * Remove a helper.
     * @param helperId The helper id to remove.
     * @return {Promise<void>}
     */
    async removeHelper(helperId) {
      try {
        const removeResponse = await this.$refs.eipJanusClient.removeSipHelper({ helperId });
        this.helpers = removeResponse.helpers;
      } catch (e) {}
    },

    handleRemoveHelper(e) {
      const { helperId } = e.detail;
      this.removeHelper(helperId);
    },

    async establishJanusSession({ servers, iceServers, debug = false }) {
      try {
        // Initialize the janus lib.
        await this.$refs.eipJanusClient.initialize({ debug });

        // Establish the session.  The arguments are optional, but take precedence over ones set at element level.
        const sessionResponse = await this.$refs.eipJanusClient.createSession({ servers, iceServers });
        if (sessionResponse) {
          // Store the janus instance for external use if needed.
          this.janus = sessionResponse.janus;
          // Attach the sip plugin
          const attachResponse = await this.$refs.eipJanusClient.attachSipPlugin();
          this.defaultCall.sipHandle = attachResponse.sipHandle;
        }
      } catch (error) {}
    }
  }
};
</script>

<style scoped></style>
